import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { GoogleFontIcon } from '../../icons/GoogleFontIcon';
import { isSupportedFileIcon, SVGIcon } from '../../icons/SVGIcon';
import { Popover } from '../../popover/Popover';
import { TextWithTooltip } from '../../tooltip/TextWithTooltip';
import { Tooltip } from '../../tooltip/Tooltip';

import { getTooltipLabel } from './getTooltipLabel';
import { WorkflowStep, WorkflowStepFile } from './workflowVisualizationModel';

import styles from './workflowVisualization.module.scss';

export type WorkflowVisualizationStepProps = {
  step: WorkflowStep;
  successor?: {
    isActive: boolean;
  };
  onStepClick?: () => void;
  onFileClick?: (file: WorkflowStepFile) => void;
};

function StepConnection({ isActive }: { isActive: boolean }) {
  return (
    <span
      data-testid="workflow-visualization-step__connection"
      className={clsx(styles['step__connection'], {
        [styles['step__connection--active']]: isActive,
        [styles['step__connection--inactive']]: !isActive,
      })}
    ></span>
  );
}

export function WorkflowVisualizationStep({
  step,
  successor,
  onStepClick,
  onFileClick,
}: WorkflowVisualizationStepProps) {
  const { t } = useTranslation();

  return (
    <div className={styles['step']}>
      <div className={styles['step__top']}>
        <Tooltip
          disabled={getTooltipLabel(step) === ''}
          label={getTooltipLabel(step)}
        >
          <span
            data-testid="workflow-visualization-step__dot"
            className={clsx(styles['step__dot'], {
              [styles['step__dot--inactive']]: !step.isTriggered,
              [styles['step__dot--active']]: step.isTriggered,
              [styles['step__clickable']]: onStepClick != null,
            })}
            onClick={onStepClick}
          />
        </Tooltip>
        {successor != null ? (
          <StepConnection isActive={successor.isActive} />
        ) : null}
      </div>
      <div className={styles['step__bottom']}>
        <span
          className={clsx(styles['step__label'], {
            [styles['step__clickable']]: onStepClick != null,
          })}
          onClick={onStepClick}
        >
          {step.title}
        </span>
        {step.files?.map((file) => {
          const virusInfected = !file.virusFree;
          const allowFileDownload = onFileClick && !virusInfected;

          return (
            <div
              onClick={allowFileDownload ? () => onFileClick(file) : undefined}
              key={file.id}
            >
              {file.virusFree !== true ? (
                <TextWithTooltip
                  tooltipProps={{
                    disabled: false,
                    label: t('app.table.tooltip.maliciousContent'),
                  }}
                >
                  <SVGIcon
                    name="file-alert"
                    className={styles['step__dot--file']}
                  />
                </TextWithTooltip>
              ) : (
                <TextWithTooltip
                  tooltipProps={{ label: file.fileName, disabled: false }}
                >
                  <SVGIcon
                    name={mapFileNameToSVGFileIcon(file.fileName)}
                    className={clsx(styles['step__dot--file'], {
                      [styles['step__clickable']]: allowFileDownload,
                    })}
                  />
                </TextWithTooltip>
              )}
            </div>
          );
        })}

        {step.comments?.map((comment, index) => (
          <div key={comment.value}>
            <Popover
              position="top"
              content={
                <div className={styles['step__dot--comment-list']}>
                  {comment.value}
                </div>
              }
            >
              <GoogleFontIcon
                data-testid={`icon-comment-${index}`}
                name="chat_bubble"
                solid
                className={clsx(
                  styles['step__dot--comment'],
                  styles['step__clickable']
                )}
              />
            </Popover>
          </div>
        ))}
      </div>
    </div>
  );
}

function mapFileNameToSVGFileIcon(fileName: string) {
  const fileIconName = `file-${fileName.split('.').pop()?.toLowerCase()}`;
  return isSupportedFileIcon(fileIconName) ? fileIconName : 'file-unknown';
}
